import { Link } from 'react-router-dom'
import { Bell, CheckSquare, Repeat, Wrench } from 'lucide-react'
import type { GoalBoardCard } from '../../services/goalService'
import { reminderWords } from '../../utils/habitWords'
import { habitReminderInput } from '../../utils/skillWords'
import { dueLine } from '../../utils/taskForm'

/**
 * Hold me to it, on a goal (Goals UX pass 2026-09-06; the same call as
 * skills): a goal is never reminded about — the tasks and habits under it
 * are, and the skills under it read their own. This block displays what each
 * live item has set and links to where it is changed. Nothing is set here.
 */
export function GoalHoldMeToIt({ card, headingClass }: { card: GoalBoardCard | null; headingClass: string }) {
  const tasks = (card?.items.tasks || []).filter((t) => t.readiness_status !== 'blocked')
  const habits = (card?.items.habits || []).filter((h) => h.live)
  const skills = card?.items.skills || []
  const rows: Array<{ key: string; kind: 'task' | 'habit'; id: number; title: string; words: string | null }> = []
  for (const t of tasks) rows.push({ key: `t${t.id}`, kind: 'task', id: t.id, title: t.title, words: dueLine(t) })
  for (const h of habits) rows.push({ key: `h${h.id}`, kind: 'habit', id: h.id, title: h.title, words: reminderWords(habitReminderInput(h)) })
  const set = rows.filter((r) => r.words)
  const unset = rows.filter((r) => !r.words)
  const restingOnly = rows.length === 0 && (card?.items.habits.length || 0) > 0
  return (
    <div data-testid="goal-hold">
      <h3 className={headingClass}><Bell className="h-3.5 w-3.5 text-brand" /> Hold me to it</h3>
      {rows.length === 0 && skills.length === 0 ? (
        <p className="mt-1.5 text-sm text-secondary" data-testid="goal-hold-empty">
          {restingOnly ? 'Its habits are paused, so nothing is reminding you right now. Reminders show here from the live tasks and habits under it.' : 'Reminders are set on the tasks and habits under this goal and show up here. Nothing is under it yet.'}
        </p>
      ) : (
        <ul className="mt-1.5 space-y-1.5 text-sm" data-testid="goal-hold-items">
          {set.map((r) => (
            <li key={r.key} className="flex items-start gap-2">
              <span className="mt-0.5 shrink-0 text-muted" aria-hidden="true">{r.kind === 'habit' ? <Repeat className="h-3.5 w-3.5" /> : <CheckSquare className="h-3.5 w-3.5" />}</span>
              <span className="min-w-0">
                <Link to={`/${r.kind}s/${r.id}`} className="font-medium text-primary hover:text-brand hover:underline">{r.title}</Link>
                <span className="block text-app-micro text-secondary">{r.words}</span>
              </span>
            </li>
          ))}
          {unset.length > 0 && (
            <li className="text-app-micro text-muted" data-testid="goal-hold-unset">
              {unset.length === 1 ? <>Nothing set yet on <Link to={`/${unset[0].kind}s/${unset[0].id}`} className="text-brand hover:underline">{unset[0].title}</Link>.</> : `Nothing set yet on ${unset.length} of them.`}
            </li>
          )}
          {/* One line for the skills (2026-09-12): "A skill holds its own reminders" was repeated per skill. */}
          {skills.length > 0 && (
            <li className="flex items-start gap-2 text-app-micro text-secondary" data-testid="goal-hold-skill">
              <span className="mt-0.5 shrink-0 text-muted" aria-hidden="true"><Wrench className="h-3.5 w-3.5" /></span>
              <span className="min-w-0">
                {skills.map((s, i) => (
                  <span key={s.id}>{i > 0 ? (i === skills.length - 1 ? ' and ' : ', ') : ''}<Link to={`/skills/${s.id}`} className="font-medium text-primary hover:text-brand hover:underline">{s.title}</Link></span>
                ))}
                {skills.length === 1 ? ' holds its own reminders, on its page.' : ' hold their own reminders, on their pages.'}
              </span>
            </li>
          )}
        </ul>
      )}
    </div>
  )
}

export default GoalHoldMeToIt
